import React, { useEffect, useState, useMemo } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { useNavigate, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { db } from "../firebase/firebase";
import { collection, getDocs } from "firebase/firestore";
import {
  Store,
  Package,
  Users,
  BarChart3,
  ArrowRightLeft,
  IndianRupee,
  TrendingUp,
  Activity,
  ArrowRight,
  X
} from "lucide-react";

export default function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState(null);

  // State for controlling the popup modal for store stock
  const [popupStore, setPopupStore] = useState(null);

  // Data States
  const [stats, setStats] = useState({
    totalStores: 0,
    totalProducts: 0,
    franchise: 0,
    revenue: 0,
  });
  const [lowStockAlerts, setLowStockAlerts] = useState([]);

  // Low stock threshold
  const LOW_STOCK_THRESHOLD = 5; 

  useEffect(() => {
    const userRole = localStorage.getItem("role");
    setRole(userRole);

    const fetchDashboardData = async () => {
      if (userRole !== "SUPER_ADMIN") {
        setLoading(false);
        return;
      }

      try {
        // 1. Fetch All Stores
        const storesSnapshot = await getDocs(collection(db, "stores"));
        const storesList = storesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        let globalRevenue = 0;
        let globalProductCount = 0;
        let alerts = [];

        // 2. Loop through stores to get Sales and Inventory
        for (const store of storesList) {
          const storeName = store.storeName || store.storeId || store.name || `Store ${store.id}`;

          // Calculate Revenue
          const salesSnap = await getDocs(collection(db, "stores", store.id, "sales"));
          salesSnap.forEach(saleDoc => {
            const saleData = saleDoc.data();
            globalRevenue += Number(saleData.financials?.netPayable || 0);
          });

          // Check Inventory for Stock Alerts
          const inventorySnap = await getDocs(collection(db, "stores", store.id, "inventory"));
          globalProductCount += inventorySnap.size;

          inventorySnap.forEach(invDoc => {
            const item = invDoc.data();
            const qty = Number(item.quantity || 0);
            
            // trackInventory is Yes, quantity is below threshold, and strictly greater than 0
            if (item.trackInventory === "Yes" && qty > 0 && qty <= LOW_STOCK_THRESHOLD) {
              alerts.push({
                id: invDoc.id,
                storeId: store.id,
                storeName: storeName,
                productName: item.productName || "Unknown Product",
                brand: item.brand || "N/A",
                quantity: qty,
                price: item.retailPrice || item.basicPrice || 0
              });
            }
          });
        }

        // Sort alerts by lowest quantity first
        alerts.sort((a, b) => a.quantity - b.quantity);

        setStats({
          totalStores: storesList.length,
          totalProducts: globalProductCount,
          franchise: 0, 
          revenue: globalRevenue,
        });
        setLowStockAlerts(alerts);

      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        loading === true && setLoading(false);
      }
    };

    fetchDashboardData();
  }, [loading]);

  // Group alerts dynamically by store for individual branch stock alert cards
  const storeWiseStockAlerts = useMemo(() => {
    const grouped = {};
    lowStockAlerts.forEach(alert => {
      if (!grouped[alert.storeId]) {
        grouped[alert.storeId] = {
          storeId: alert.storeId,
          storeName: alert.storeName,
          items: []
        };
      }
      grouped[alert.storeId].items.push(alert);
    });
    return Object.values(grouped);
  }, [lowStockAlerts]);

  // Loading Screen (Light Theme)
  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mb-4"></div>
        <div className="text-slate-600 text-lg font-bold tracking-wider uppercase">
          Syncing Global Data...
        </div>
      </div>
    );
  }

  // Access Control
  if (role !== "SUPER_ADMIN") {
    return <Navigate to="/" replace />;
  }

  const actionCards = [
    { title: "Create Store", icon: Store, color: "from-blue-600 to-cyan-500", action: () => navigate("/create-store") },
    { title: "Store Reports", icon: BarChart3, color: "from-purple-600 to-pink-500", action: () => navigate("/stores") },
    { title: "Closing Stock", icon: Package, color: "from-orange-500 to-red-500", action: () => navigate("/report/closing-stock") },
    { title: "Sales Report", icon: IndianRupee, color: "from-emerald-500 to-teal-600", action: () => navigate("/sales") },
    { title: "Inventory Transfer", icon: ArrowRightLeft, color: "from-amber-500 to-orange-500", action: () => navigate("/inventory") },
    { title: "Login Report", icon: Users, color: "from-indigo-500 to-violet-600", action: () => navigate("/login-report") },
  ];

  return (
    <div className="flex min-h-screen bg-slate-50 font-sans antialiased text-slate-800">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-x-hidden relative">
        <div className="p-6 md:p-8 flex-1 space-y-8">
          
          {/* Dashboard Header */}
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <h1 className="text-3xl font-black text-slate-800 flex items-center gap-3 uppercase tracking-tight">
              <Activity className="text-blue-600" size={32} /> 
              Super Admin Dashboard
            </h1>
            <p className="text-slate-500 font-bold text-sm mt-1 uppercase tracking-wider">
              Shruti Eye Care & Opticals ERP System
            </p>
          </motion.div>

          {/* Top KPI Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
            <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm flex flex-col justify-center">
              <div className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-2">
                <Store size={14}/> Total Active Stores
              </div>
              <div className="text-4xl font-black text-slate-700">{stats.totalStores}</div>
            </div>

            <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm flex flex-col justify-center">
              <div className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-2">
                <Package size={14}/> Total Products
              </div>
              <div className="text-4xl font-black text-slate-700">{stats.totalProducts.toLocaleString()}</div>
            </div>

            <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm flex flex-col justify-center">
              <div className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-2">
                <Users size={14}/> Franchise Partners
              </div>
              <div className="text-4xl font-black text-slate-700">{stats.franchise}</div>
            </div>

            <div className="bg-emerald-50 border border-emerald-200 p-6 rounded-3xl shadow-sm flex flex-col justify-center">
              <div className="text-emerald-600 text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-2">
                <TrendingUp size={14}/> Global Revenue
              </div>
              <div className="text-4xl font-black text-emerald-700">₹{stats.revenue.toLocaleString()}</div>
            </div>
          </div>

          {/* Action Cards Grid */}
          <div className="w-full space-y-6">
            <h2 className="text-sm font-black uppercase text-slate-800 tracking-wider">Quick Actions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {actionCards.map((card, index) => {
                const Icon = card.icon;
                return (
                  <motion.button
                    key={card.title}
                    onClick={card.action}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className={`bg-gradient-to-r ${card.color} p-6 rounded-3xl shadow-md text-left text-white relative overflow-hidden group`}
                  >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-12 -mt-12 transition-transform group-hover:scale-110"></div>
                    <Icon size={36} className="mb-4 relative z-10 opacity-90" />
                    <h3 className="text-lg font-black relative z-10 tracking-wide">{card.title}</h3>
                    <p className="text-white/80 mt-1 text-xs font-bold relative z-10">Manage {card.title.toLowerCase()}</p>
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* Storewise Detailed Stock Alert Summary Cards */}
          <div className="space-y-4">
            <h2 className="text-sm font-black uppercase text-slate-800 tracking-wider">Store-wise Stock Alerts</h2>
            {storeWiseStockAlerts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {storeWiseStockAlerts.map((storeCard) => {
                  
                  const displayedItems = storeCard.items.slice(0, 5);

                  return (
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      key={storeCard.storeId}
                      className="bg-white border border-slate-200 rounded-3xl p-5 shadow-sm space-y-4 flex flex-col justify-between"
                    >
                      <div>
                        <div className="flex justify-between items-start border-b border-slate-100 pb-3">
                          <div>
                            <h3 className="text-sm font-black text-slate-800 uppercase tracking-wide">{storeCard.storeId}</h3>
                            <p className="text-[10px] font-bold text-slate-400 uppercase mt-0.5">Critical Inventory Risks</p>
                          </div>
                          <span className="bg-amber-100 text-amber-800 text-[10px] font-black px-2.5 py-1 rounded-full">
                            {storeCard.items.length} Flags
                          </span>
                        </div>

                        <div className="divide-y divide-slate-50 mt-3 pr-1">
                          {displayedItems.map(item => (
                            <div key={item.id} className="py-2.5 flex justify-between items-center text-xs">
                              <div className="truncate pr-2">
                                <p className="font-bold text-slate-700 truncate">{item.productName}</p>
                                <p className="text-[10px] text-slate-400 font-medium font-mono">{item.brand}</p>
                              </div>
                              <div className="text-right shrink-0">
                                <span className="font-black text-amber-600 bg-amber-50 px-2 py-0.5 rounded-md font-mono">{item.quantity} units</span>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* View All Button triggers Popup */}
                        {storeCard.items.length > 5 && (
                          <button
                            onClick={() => setPopupStore(storeCard)}
                            className="text-xs font-bold text-blue-600 hover:text-blue-800 mt-2 uppercase tracking-wide"
                          >
                            View All ({storeCard.items.length})
                          </button>
                        )}
                      </div>

                      <button 
                        onClick={() => navigate("/stores")}
                        className="w-full mt-2 py-2 bg-slate-50 hover:bg-blue-50 text-slate-600 hover:text-blue-700 border border-slate-100 hover:border-blue-200 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-1.5"
                      >
                        Inspect Store Logs <ArrowRight size={14}/>
                      </button>
                    </motion.div>
                  )
                })}
              </div>
            ) : (
              <div className="bg-white border border-slate-200 p-8 rounded-3xl text-center text-slate-400 font-bold text-xs uppercase tracking-wider">
                All branch networks have completely stable stock profiles.
              </div>
            )}
          </div>
        </div>

        {/* POPUP MODAL FOR "VIEW ALL" STOCK */}
        <AnimatePresence>
          {popupStore && (
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ type: "spring", duration: 0.4 }}
                className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[85vh]"
              >
                {/* Modal Header */}
                <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50 sticky top-0 z-10">
                  <div>
                    <h3 className="text-lg font-black text-slate-800 uppercase tracking-wide">{popupStore.storeName}</h3>
                    <p className="text-xs font-bold text-amber-600 uppercase mt-0.5">
                      Critical Inventory Risks ({popupStore.items.length} Items)
                    </p>
                  </div>
                  <button 
                    onClick={() => setPopupStore(null)} 
                    className="p-2 bg-white border border-slate-200 hover:bg-slate-100 rounded-full text-slate-500 transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>
                
                {/* Modal Body (Scrollable List) */}
                <div className="p-2 overflow-y-auto custom-scrollbar bg-white">
                  <div className="divide-y divide-slate-50">
                    {popupStore.items.map(item => (
                      <div key={item.id} className="p-4 flex justify-between items-center hover:bg-slate-50 transition-colors rounded-xl">
                        <div className="flex items-start gap-3">
                          <div className="bg-amber-100 p-2 rounded-xl text-amber-600 mt-0.5">
                            <Package size={16} />
                          </div>
                          <div className="pr-2">
                            <p className="text-sm font-bold text-slate-800">{item.productName}</p>
                            <p className="text-xs text-slate-400 font-medium font-mono mt-0.5">{item.brand}</p>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <span className="font-black text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg text-sm border border-amber-100">
                            {item.quantity} units
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="p-4 border-t border-slate-100 bg-slate-50 mt-auto">
                  <button 
                    onClick={() => setPopupStore(null)} 
                    className="w-full py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-sm font-black uppercase tracking-wider transition-colors"
                  >
                    Close Window
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </div>
  );
}